import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import { useTranslator } from 'tradera/lang/translate';
import { ProfileHeader } from '../components/profile/profile-header/profile-header';
import { ProfileInfo } from '../components/profile/profile-info';
import { useFetchData } from 'tradera/hooks/use-fetch-data';
import { usePageMeta } from 'tradera/hooks/use-page-meta';
import SearchResultsWrapper from '../components/discover/containers/search-results-wrapper';
import ProfileFeedbackPage from 'tradera/apps/tradera/pages/profile-feedback-page';
import { OPTIONAL_LANG } from 'tradera/apps/tradera/helpers/optional-lang';
import { getProfile } from '../components/profile/profile-header/api';
import { MEMBER_ACCOUNT_STATE } from '../components/profile/member-account-state';
import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import PAGE_TYPES from 'tradera/constants/page-types';

function ProfilePage(props) {
    const {
        loadStatus,
        location,
        pageType,
        publicProfileResponse,
        match,
        metaRobots,
        metaDescription,
        metaTitle
    } = props;
    const { t } = useTranslator();
    const { memberId, profilePageType } = match?.params;

    const [pageMetaData, setPageMetaData] = useState({
        metaTitle: metaTitle,
        metaDescription: metaDescription,
        metaRobots: metaRobots
    });

    const shouldFetchProfile =
        publicProfileResponse?.profileMemberId != memberId;

    const { data, isLoading, isRejected, retryFetch } = useFetchData(
        shouldFetchProfile ? getProfile : null,
        memberId,
        'Profile Details'
    );

    const profileData = shouldFetchProfile ? data : publicProfileResponse;

    useEffect(() => {
        if (profileData?.alias) {
            const metaData = getProfilePageMeta(
                profileData,
                profilePageType,
                t
            );
            setPageMetaData(metaData);
        }
    }, [profilePageType, profileData, t]);

    usePageMeta(`${pageType}_${profilePageType}`, pageMetaData, false);

    // This page route configuration has mustLoadDataBeforePageTracking=true to
    // support the items page, for other sub pages we need to call setLoaded here.
    const setLoaded = loadStatus.setLoaded;
    useEffect(() => {
        if (profilePageType === 'items') {
            return;
        }
        setLoaded();
    }, [setLoaded, profilePageType]);

    const { accountState } = profileData;

    if (accountState === MEMBER_ACCOUNT_STATE.DEREGISTERED) {
        return (
            <div className="container">
                <div className="row">
                    <div
                        className="alert alert-danger col-auto d-flex mx-auto my-3"
                        role="alert">
                        <AlertIcon className="icon icon-md mr-2 my-auto" />
                        <a
                            className="text-white"
                            href="http://info.tradera.com/hjalp/vad-innebar-kontot-ar-avaktiverat-av-tradera/">
                            <h1 className="heading-london mb-0">
                                {t('profile_memberDeregistered')}
                            </h1>
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {profileData && (
                <ProfileHeader
                    isLoading={isLoading}
                    isRejected={isRejected}
                    profileData={profileData}
                    retryFetch={retryFetch}
                />
            )}
            <Switch>
                <Route
                    path={`${OPTIONAL_LANG}/profile/items/:memberId/:aliasSlug`}
                    render={() => {
                        return (
                            <SearchResultsWrapper
                                pageType={PAGE_TYPES.CUSTOM_RESULT_PAGE}
                                searchPath={location.pathname}
                                showHeading={false}
                                loadStatus={loadStatus}
                            />
                        );
                    }}
                />
                <Route
                    path={`${OPTIONAL_LANG}/profile/feedback/:memberId/:aliasSlug`}
                    render={(props) => {
                        return <ProfileFeedbackPage {...props} />;
                    }}
                />
                <Route
                    path={`${OPTIONAL_LANG}/profile/information/:memberId/:aliasSlug`}
                    render={() => {
                        return <ProfileInfo memberId={memberId} />;
                    }}
                />
            </Switch>
        </>
    );
}

const getProfilePageMeta = (profileData, profilePageType, t) => {
    const { alias, hasShop, memberSince } = profileData;

    switch (profilePageType) {
        case 'items':
            return {
                metaTitle: t(`pageMeta_title_ProfilePage_items`, {
                    memberAlias: alias
                }),
                metaDescription: t(`pageMeta_description_ProfilePage_items`, {
                    memberAlias: alias,
                    memberSince: memberSince
                }),
                metaRobots: hasShop ? '' : 'noindex'
            };
        case 'feedback':
            return {
                metaTitle: t(`pageMeta_title_ProfilePage_feedback`, {
                    memberAlias: alias
                }),
                metaDescription: t(
                    `pageMeta_description_ProfilePage_feedback`,
                    {
                        memberAlias: alias,
                        memberSince: memberSince
                    }
                ),
                metaRobots: 'noindex'
            };
        case 'information':
            return {
                metaTitle: t(`pageMeta_title_ProfilePage_info`, {
                    memberAlias: alias
                }),
                metaDescription: t(`pageMeta_description_ProfilePage_info`, {
                    memberAlias: alias,
                    memberSince: memberSince
                }),
                metaRobots: 'noindex'
            };
        default:
            break;
    }
};

const mapStateToProps = (state) => {
    return {
        metaDescription: state.initData.description,
        metaRobots: state.initData.metaRobots,
        metaTitle: state.initData.title,
        publicProfileResponse: state.initData.publicProfileResponse,
        siteLayout: state.ui.siteLayout
    };
};

export default connect(mapStateToProps)(ProfilePage);
